import { useCallback } from 'react';
import { logError } from '@zg-rentals/log-error';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { useModalOpener } from 'app/shared/contexts/ModalContext/useModal';
import {
  emitTrackHdpNativeShareEvent,
  emitTrackShareHomeEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';

type ShareableListing = {
  uriV2?: string;
  [key: string]: unknown;
};

type ShareHomeTriggerLocation = 'home_details' | 'search_results';
type ShareHomeTriggerObject = 'home_details_component|action_bar' | 'property_card';

type UseShareActionOptions = {
  listing: ShareableListing | ListingDetails | null | undefined;
  triggerLocation: ShareHomeTriggerLocation;
  triggerObject: ShareHomeTriggerObject;
};

export function buildShareUrl(listing: { uriV2?: string }, source: string, medium: string): string {
  const base = `https://hotpads.com${listing.uriV2 ?? ''}`;
  const params = new URLSearchParams({
    utm_medium: medium,
    utm_source: source,
    utm_campaign: 'tellafriend',
  });
  return `${base}?${params.toString()}`;
}

export const useShareAction = ({ listing, triggerLocation, triggerObject }: UseShareActionOptions) => {
  const { openShareFormModal } = useModalOpener();

  const openShare = useCallback(() => {
    if (!listing || !listing.uriV2) return;

    emitTrackShareHomeEvent({
      listing: listing as ListingDetails,
      triggerLocation,
      triggerObject,
    });

    const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;

    if (isMobileViewport && navigator.share) {
      const shareUrl = buildShareUrl(listing, 'native_share', 'referral');
      navigator
        .share({
          title: 'HotPads listing',
          text: 'Check out this listing I found on HotPads!',
          url: shareUrl,
        })
        .then(() => {
          emitTrackHdpNativeShareEvent();
        })
        .catch((err) => {
          if (err instanceof Error && err.name !== 'AbortError') {
            logError({ error: err, context: 'Error sharing via Web Share API' });
          }
        });
    } else {
      openShareFormModal({ props: { listing } });
    }
  }, [listing, triggerLocation, triggerObject, openShareFormModal]);

  return openShare;
};
